/**
 * FileWriteCard — renders a `file:<path>` code block in the AI Chat as a
 * card with an "Apply to project" button. Replaces a plain <pre><code>
 * block when the LLM emits the file-write protocol:
 *
 *   ```file:src/Hello.tsx
 *   export const Hello = () => <div>Hello</div>;
 *   ```
 *
 * Click Apply → projectStore.writeFile(path, content) → PUT
 * /api/projects/{id}/files/{path} → file tree refresh.
 *
 * Created: 2026-05-06.
 */
import { useRef, useState } from 'react';
import { FileCode, Check, Loader2, AlertCircle } from 'lucide-react';
import { useProjectStore } from '@/stores/projectStore';

interface FileWriteCardProps {
  path: string;
  children: React.ReactNode;
  // 2026-05-06 truncation-bundle Fix #3 (R1 reviewer C4 + C5):
  // when the parent message was truncated (vLLM finish_reason="length"
  // or other non-"stop") OR the message is still streaming, the Apply
  // button must be disabled. Pre-fix, clicking Apply on a truncated
  // file block wrote a partial source file that wouldn't compile.
  messageFinishReason?: string;
  isStreaming?: boolean;
}

type ApplyStatus = 'idle' | 'applying' | 'applied' | 'error';

export function FileWriteCard({
  path,
  children,
  messageFinishReason,
  isStreaming,
}: FileWriteCardProps) {
  const preRef = useRef<HTMLPreElement>(null);
  const writeFile = useProjectStore((s) => s.writeFile);
  const currentProject = useProjectStore((s) => s.currentProject);
  const [status, setStatus] = useState<ApplyStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    // R2 reviewer C1 (2026-05-06): application-layer guard. The
    // disabled={isDisabled} HTML attr blocks normal clicks but not
    // programmatic .click() calls (browser extensions, accessibility
    // tooling, future direct callers). Compute the same guard here
    // so handleApply is safe regardless of how it's invoked.
    if (isDisabled) {
      return;
    }
    const code = preRef.current?.querySelector('code');
    const content = code?.textContent ?? '';
    if (!currentProject) {
      setStatus('error');
      setError('No active project — open or create a project first.');
      return;
    }
    setStatus('applying');
    setError(null);
    try {
      await writeFile(path, content);
      setStatus('applied');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setStatus('error');
    }
  };

  // 2026-05-06 truncation-bundle Fix #3: identify reasons to prevent the
  // user from clicking Apply on a partial file. Streaming-in-flight and
  // truncated-completion are both classes of "the content the user would
  // write may be incomplete / mid-token". Disabled-with-explanation is
  // safer than allowing a click that writes a broken file to disk.
  const isTruncated = !!messageFinishReason && messageFinishReason !== 'stop';
  const blockReason: string | null = isStreaming
    ? 'Streaming — wait for the response to finish before applying'
    : isTruncated
      ? `Response was truncated (${messageFinishReason}); this file may be incomplete`
      : null;

  const buttonLabel: Record<ApplyStatus, React.ReactNode> = {
    idle: blockReason
      ? isStreaming
        ? 'Streaming…'
        : 'Truncated — incomplete'
      : 'Apply to project',
    applying: (
      <>
        <Loader2 className="w-3 h-3 animate-spin" />
        Applying…
      </>
    ),
    applied: (
      <>
        <Check className="w-3 h-3" />
        Applied
      </>
    ),
    error: 'Retry',
  };

  const isDisabled =
    status === 'applying' ||
    status === 'applied' ||
    blockReason !== null;

  return (
    <div className="my-2 rounded overflow-hidden border border-vibe-accent/40">
      <div className="flex items-center justify-between bg-vibe-bg-tertiary px-3 py-1.5 text-xs">
        <span className="flex items-center gap-1.5 font-mono text-vibe-text">
          <FileCode className="w-3.5 h-3.5 text-vibe-accent" />
          {path}
        </span>
        <button
          type="button"
          onClick={handleApply}
          disabled={isDisabled}
          className={
            status === 'applied'
              ? 'flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-success/20 text-vibe-success cursor-default'
              : status === 'error'
                ? 'flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-error/20 text-vibe-error hover:bg-vibe-error/30'
                : blockReason
                  ? 'flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-warning/20 text-vibe-warning cursor-not-allowed'
                  : 'flex items-center gap-1 px-2 py-1 rounded text-xs bg-vibe-accent text-white hover:bg-vibe-accent-hover disabled:opacity-50'
          }
          title={
            blockReason ??
            (status === 'applied' ? 'File written' : 'Write this file to the project')
          }
        >
          {buttonLabel[status]}
        </button>
      </div>
      <pre ref={preRef} className="bg-vibe-bg p-3 overflow-x-auto text-sm">
        {children}
      </pre>
      {/* R2 reviewer C2 (2026-05-06): hide the warning banner after the
          user has explicitly chosen to Apply. Once status transitions to
          'applied', the user has acknowledged + acted on the truncation
          warning; continuing to show it post-action is alarm-fatigue. */}
      {blockReason && status !== 'applied' && (
        <div className="flex items-start gap-1.5 px-3 py-1.5 bg-vibe-warning/10 text-xs text-vibe-warning border-t border-vibe-warning/30">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{blockReason}</span>
        </div>
      )}
      {status === 'error' && error && (
        <div className="flex items-start gap-1.5 px-3 py-1.5 bg-vibe-error/10 text-xs text-vibe-error border-t border-vibe-error/30">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="break-all">{error}</span>
        </div>
      )}
    </div>
  );
}
